import { Button, Checkbox, IconButton, TextField } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/shared/ui/atoms';

import { useEditCourseAccess } from '../../../hooks/useEditCourseAccess';
import type { CourseAccess } from '../../../types/enrollment';

// ─── Props ───────────────────────────────────────────────────────────────────

/**
 * Props do drawer de edição do acesso ao curso de uma matrícula.
 */
export interface EnrollmentCourseAccessDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly enrollmentId: number;
  readonly courseAccess: CourseAccess;
  readonly startDate: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

/**
 * Drawer lateral de edição do acesso ao curso de uma matrícula.
 * Permite alterar isReleased, releasedUntil e releasedDays e persiste via PUT.
 */
export function EnrollmentCourseAccessDrawer({
  isOpen,
  onClose,
  enrollmentId,
  courseAccess,
  startDate,
}: EnrollmentCourseAccessDrawerProps) {
  const { t } = useTranslation('personManagement');
  const { form, initForm, updateForm, save, isSaving } = useEditCourseAccess(enrollmentId, onClose);

  // Campos só ficam editáveis quando o acesso está liberado (checkbox marcado).
  const fieldsEnabled = form.isReleased;
  const hasDate = form.releasedUntil !== '';
  const hasDays = form.releasedDays !== '';

  // Data e dias adicionais são mutuamente exclusivos: ao preencher um, o outro é
  // desabilitado (e limpo). A checagem `&& !esteCampo` evita travar ambos quando
  // a matrícula já vem com os dois valores preenchidos.
  const dateDisabled = !fieldsEnabled || (hasDays && !hasDate);
  const daysDisabled = !fieldsEnabled || (hasDate && !hasDays);

  // A data, quando informada, precisa ser posterior ao dia de hoje.
  const isDateInPast = hasDate && !dayjs(form.releasedUntil).isAfter(dayjs(), 'day');
  const hasDateError = fieldsEnabled && isDateInPast;

  // Ao escolher a data, limpa os dias adicionais (e vice-versa) para manter a
  // exclusividade entre os dois campos.
  const handleDateChange = (value: string) => {
    updateForm('releasedUntil', value);
    if (value !== '') updateForm('releasedDays', '');
  };

  const handleDaysChange = (value: string) => {
    updateForm('releasedDays', value);
    if (value !== '') updateForm('releasedUntil', '');
  };

  // Inicializa o form com os valores atuais toda vez que o drawer abre
  useEffect(() => {
    if (isOpen) {
      initForm(courseAccess);
    }
  }, [isOpen, courseAccess, initForm]);

  // Fecha com Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Trava o scroll do body
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-none z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-none bg-base-neutral-black/medium"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Painel */}
      <div className="relative ml-auto flex h-full w-full max-w-[32rem] flex-col bg-ctx-layout-surface shadow-level3">
        {/* Header */}
        <div className="flex items-start justify-between border-b-hairline border-ctx-layout-border px-xxs py-xxxs">
          <div className="flex flex-col gap-quark">
            <h2 className="ts-heading-sm text-ctx-content-title">
              {t('enrollmentAcademic.courseAccessDrawer.title')}
            </h2>
          </div>
          <IconButton
            icon={XMarkIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('enrollmentAcademic.courseAccessDrawer.cancelButton')}
            onClick={onClose}
          />
        </div>

        {/* Corpo scrollável */}
        <div className="flex-1 overflow-y-auto px-xxs py-xxxs">
          <div className="flex flex-col gap-xxs">
            {/* Acesso liberado */}
            <Checkbox
              label={t('enrollmentAcademic.courseAccessDrawer.fieldReleased')}
              checked={form.isReleased}
              onCheckedChange={v => {
                if (v !== 'indeterminate') updateForm('isReleased', v);
              }}
            />

            {/* Liberado até */}
            <DatePicker
              label={t('enrollmentAcademic.courseAccessDrawer.fieldReleasedUntil')}
              value={form.releasedUntil}
              onChange={handleDateChange}
              disabled={dateDisabled}
              hasError={hasDateError}
              helper={
                hasDateError
                  ? t('enrollmentAcademic.courseAccessDrawer.fieldReleasedUntilError')
                  : t('enrollmentAcademic.courseAccessDrawer.fieldReleasedUntilHelper')
              }
            />

            {/* Dias adicionais */}
            <TextField
              label={t('enrollmentAcademic.courseAccessDrawer.fieldReleasedDays')}
              value={form.releasedDays}
              onChange={e => handleDaysChange(e.target.value)}
              placeholder={t('enrollmentAcademic.courseAccessDrawer.fieldReleasedDaysPlaceholder')}
              helper={t('enrollmentAcademic.courseAccessDrawer.fieldReleasedDaysHelper')}
              type="number"
              disabled={daysDisabled}
            />

            {/* Iniciou em — somente leitura */}
            <div className="flex flex-col gap-quark">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('enrollmentAcademic.courseAccessDrawer.fieldStartDateLabel')}
              </span>
              <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                {formatDate(startDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-xxs border-t-hairline border-ctx-layout-border px-xxs py-xxxs">
          <Button hierarchy="secondary" size="sm" onClick={onClose} disabled={isSaving}>
            {t('enrollmentAcademic.courseAccessDrawer.cancelButton')}
          </Button>
          <Button size="sm" onClick={save} isLoading={isSaving} disabled={hasDateError}>
            {t('enrollmentAcademic.courseAccessDrawer.saveButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
